import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Canada', 'Australia', 
  'Singapore', 'New Zealand', 'Japan', 'South Korea', 'Hong Kong', 
  'Germany', 'France', 'Netherlands', 'Ireland', 'Sweden', 
  'Switzerland', 'United Arab Emirates', 'Saudi Arabia', 'South Africa'
];

export default function ComplianceChatbot() {
  const [country, setCountry] = useState('Singapore');
  const [messages, setMessages] = useState([
    { role: 'model', content: 'Select a jurisdiction and ask me a question about AI governance or data privacy.' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // 🌟 State for inline "Copied" feedback
  const [copiedId, setCopiedId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null); 

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || !country) return;

    const userMessage = { role: 'user', content: input };
    const currentHistory = [...messages];
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const apiHistory = currentHistory.filter((_, idx) => idx !== 0);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const res = await axios.post(`${baseURL}/api/compliance/chat`, {
        country: country,
        message: userMessage.content,
        history: apiHistory
      });

      setMessages(prev => [...prev, { role: 'model', content: res.data.reply }]);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'model', 
        content: `Error: ${err.response?.data?.error || 'Failed to connect to AI.'}` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // 🌟 Corrected Copy Handler (Now inside the component)
  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id); // Set the current message to 'copied'
      
      // Revert back to the normal copy icon after 2 seconds
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleEdit = (text) => {
    setInput(text);
    if (inputRef.current) {
      inputRef.current.focus(); // Auto-focuses the input field
    }
  };

  return (
    <div className="flex flex-col h-[600px] max-w-3xl mx-auto bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
      
      {/* Header */}
      <div className="bg-slate-900 p-4 flex justify-between items-center text-white">
        <div>
          <h2 className="font-bold text-lg">AI Governance Copilot</h2>
          <p className="text-xs text-slate-300">Vector-less Regulatory Analysis</p>
        </div>
        <select 
          className="bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5 outline-none"
          value={country}
          onChange={(e) => {
            setCountry(e.target.value);
            setMessages([{ role: 'model', content: `Context switched to ${e.target.value}. How can I assist you with local compliance?` }]);
          }}
        >
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
            <div className={`relative max-w-[85%] flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              
              {/* Message Bubble */}
              <div className={`rounded-2xl px-4 py-3 text-sm shadow-sm ${
                msg.role === 'user' 
                  ? 'bg-blue-600 text-white rounded-br-none' 
                  : 'bg-white border border-slate-200 text-slate-800 rounded-bl-none'
              }`}>
                {msg.role === 'user' ? (
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                ) : (
                  <div className="prose prose-sm max-w-none prose-slate prose-p:leading-relaxed prose-tables:text-xs prose-th:bg-slate-50 prose-td:p-2 prose-th:p-2 prose-td:border-t">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                )}

                {/* Bot bubble copy symbol (Always visible) */}
                {msg.role !== 'user' && idx !== 0 && (
                  <button 
                    onClick={() => handleCopy(msg.content, idx)}
                    className="mt-3 text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider"
                    title="Copy Response"
                  >
                    {copiedId === idx ? (
                      <span className="text-emerald-600 flex items-center gap-1"><span>✅</span> Copied!</span>
                    ) : (
                      <span className="flex items-center gap-1"><span>📋</span> Copy</span>
                    )}
                  </button>
                )}
              </div>

              {/* User bubble symbols (Always visible, Horizontal) */}
              {msg.role === 'user' && (
                <div className="flex flex-row items-center gap-1 pt-1">
                  <button 
                    onClick={() => handleEdit(msg.content)}
                    className="p-1.5 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition-colors flex items-center justify-center"
                    title="Edit Question"
                  >
                    <span className="text-xs">✏️</span>
                  </button>
                  <button 
                    onClick={() => handleCopy(msg.content, idx)}
                    className="p-1.5 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition-colors flex items-center justify-center"
                    title="Copy Question"
                  >
                    {copiedId === idx ? (
                      <span className="text-xs">✅</span>
                    ) : (
                      <span className="text-xs">📋</span>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-none px-4 py-3 text-sm text-slate-600 shadow-sm flex items-center gap-2.5">
              <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="animate-pulse font-medium">Analyzing frameworks...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <form onSubmit={handleSend} className="p-4 bg-white border-t border-slate-200 flex gap-3">
        <input 
          ref={inputRef} 
          type="text" 
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Ask about ${country} regulations...`}
          className="flex-1 border border-slate-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
          disabled={isLoading}
        />
        <button 
          type="submit" 
          disabled={isLoading || !input.trim()}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2.5 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center min-w-[80px]"
        >
          {isLoading ? (
             <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
               <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
               <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
             </svg>
          ) : 'Send'}
        </button>
      </form>
    </div>
  );
}